import { Col, DatePicker, Form, notification, Row, Space } from 'antd';
import moment from 'moment';
import { FC, memo, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';

import ButtonWrap from '@/components/ButtonWrap';
import CheckBoxGroupWrap from '@/components/CheckBoxGroupWrap';
import CheckBoxWrap from '@/components/CheckBoxWrap';
import FormItemWrap from '@/components/FormItemWrap';
import InputOnlyNumber from '@/components/InputOnlyNumber';
import InputWrap from '@/components/InputWrap';
import SelectWrap from '@/components/SelectWrap';
import { prefectures } from '@/constants/prefectures';
import { staffDivision } from '@/constants/staffDivision';
import { pattern } from '@/constants/validate';
import { RequestStaffDTO } from '@/dto/requestDto/request-staff.dto';
import { appRoutes } from '@/router/routes';
import staffService from '@/services/staff.service';
import storeService from '@/services/store.service';
import { handleMessageError } from '@/util/handleMessageError';

import './style.scss';

interface IProps {
    setTitle: any;
}

const FormData: FC<IProps> = ({ setTitle }) => {
    const { t } = useTranslation();
    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm<any>();
    const navigate = useNavigate();
    const { id } = useParams();
    const queryClient = useQueryClient();

    const { data: storeItem } = useQuery(['store', id], () => staffService.detail(String(id)), {
        enabled: !!id,
        keepPreviousData: false,
        onError: (error: any) => {
            handleMessageError(error, t);
        },
    });

    const { data: stores = [] } = useQuery(['stores'], () => storeService.getList({}), {
        keepPreviousData: true,
        onError: (error: any) => {
            handleMessageError(error, t);
        },
    });

    const plainOptions = stores?.data?.map((store: any) => ({
        label: store.name,
        value: store.id,
    }));

    const onGoList = () => {
        reset({});
        navigate(`/${appRoutes.MASTER_MANAGEMENT.STAFF.ROOT}`, { replace: true });
    };

    const { mutate: mutateCreate, isLoading: isLoadingCreate } = useMutation(
        (e: RequestStaffDTO) => staffService.create(e),
        {
            onSuccess: () => {
                onGoList();
                queryClient.removeQueries('staff');
                notification.success({
                    message: t('SUCCESS'),
                    description: `${t('SUCCESS_CREATE', { name: t('response:STAFF') })}`,
                });
            },
            onError: (error: any) => {
                handleMessageError(error, t);
            },
        },
    );

    const { mutate: mutateUpdate, isLoading: isLoadingUpdate } = useMutation(
        (e: RequestStaffDTO) => staffService.update(String(id), e),
        {
            onSuccess: () => {
                onGoList();
                queryClient.removeQueries('staff');
                notification.success({
                    message: t('SUCCESS'),
                    description: `${t('SUCCESS_EDIT', { name: t('response:STAFF') })}`,
                });
            },
            onError: (error: any) => {
                handleMessageError(error, t);
            },
        },
    );

    const onSubmit = (data: any) => {
        const staffAreas = data?.stores?.map((store: any) => ({
            store,
            note: '',
        }));

        const startWorkDate = data?.joiningTime?.length && data.joiningTime[0];
        const endWorkDate = data?.joiningTime?.length && data.joiningTime[1];
        delete data.joiningTime;
        delete data?.stores;

        const workDate =
            startWorkDate && endWorkDate
                ? {
                      startWorkDate,
                      endWorkDate,
                  }
                : {};

        id ? mutateUpdate({ ...data, staffAreas, ...workDate }) : mutateCreate({ ...data, staffAreas, ...workDate });
    };

    useEffect(() => {
        if (storeItem?.data) {
            const storesCheckBox = storeItem.data?.staffArea?.map((value: any) => value?.store?.id) || [];
            reset({
                staffCode: storeItem?.data?.staffCode,
                staffNumber: storeItem?.data?.staffNumber,
                name: storeItem?.data?.name,
                kanaName: storeItem?.data?.kanaName,
                storeManagerAuthority: storeItem?.data?.storeManagerAuthority,
                city: storeItem?.data?.city,
                conscious: storeItem?.data?.conscious,
                email: storeItem?.data?.email,
                faxNum: storeItem?.data?.faxNum,
                phoneNumber: storeItem?.data?.phoneNumber,
                prefectures: storeItem?.data?.prefectures,
                staffDivision: storeItem?.data?.staffDivision,
                postCode: storeItem?.data?.postCode,
                sortOrder: storeItem?.data?.sortOrder,
                joiningTime:
                    storeItem?.data?.startWorkDate && storeItem?.data?.endWorkDate
                        ? [moment(storeItem?.data?.startWorkDate), moment(storeItem?.data?.endWorkDate)]
                        : null,
                stores: storesCheckBox,
            });
        }
    }, [storeItem?.data, reset]);

    useEffect(() => {
        if (id) {
            setTitle(t('staff:updated_staff'));
        } else {
            setTitle(t('staff:create_staff'));
        }
    }, [id, setTitle, t]);

    return (
        <div className="p-register-staff__form">
            <Form labelCol={{ span: 6 }} onFinish={handleSubmit(onSubmit)}>
                <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                    <FormItemWrap
                        label={t('STAFF_CODE')}
                        required
                        validateStatus={errors?.staffCode ? 'error' : ''}
                        help={
                            (String(errors?.staffCode?.type) === 'required' &&
                                t('VALIDATION.REQUIRED', { name: t('STAFF_CODE') })) ||
                            (String(errors?.staffCode?.type) === 'pattern' &&
                                t('VALIDATION.NUMBER_INVALID', { name: t('STAFF_CODE') }))
                        }
                    >
                        <Controller
                            name="staffCode"
                            control={control}
                            rules={{
                                required: true,
                                pattern: pattern.inputNumber,
                            }}
                            render={({ field }) => <InputOnlyNumber field={field} disabled={!!id} />}
                        />
                    </FormItemWrap>
                    <FormItemWrap label={t('staff:STAFF_NUMBER')}>
                        <Controller
                            name="staffNumber"
                            control={control}
                            render={({ field }) => <InputOnlyNumber field={field} maxLength={6} />}
                        />
                    </FormItemWrap>
                    <FormItemWrap
                        label={t('STAFF_DIVISION')}
                        required
                        validateStatus={errors?.staffDivision ? 'error' : ''}
                        help={
                            String(errors?.staffDivision?.type) === 'required' &&
                            t('VALIDATION.REQUIRED_SELECT', { name: t('STAFF_DIVISION') })
                        }
                    >
                        <Controller
                            name="staffDivision"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                                <SelectWrap
                                    field={field}
                                    options={staffDivision?.map((division) => ({
                                        id: division,
                                        value: division,
                                    }))}
                                />
                            )}
                        />
                    </FormItemWrap>
                    <FormItemWrap
                        label={t('STAFF_NAME')}
                        required
                        validateStatus={errors?.name ? 'error' : ''}
                        help={
                            (String(errors?.name?.type) === 'required' &&
                                t('VALIDATION.REQUIRED', { name: t('STAFF_NAME') })) ||
                            (String(errors?.name?.type) === 'maxLength' &&
                                t('VALIDATION.MAXLENGTH', {
                                    name: t('STAFF_NAME'),
                                    maxLength: 20,
                                }))
                        }
                    >
                        <Controller
                            name="name"
                            control={control}
                            rules={{
                                required: true,
                                maxLength: 20,
                            }}
                            render={({ field }) => <InputWrap field={field} />}
                        />
                    </FormItemWrap>
                    <FormItemWrap
                        label={t('staff:STAFF_NAME_KANA')}
                        required
                        validateStatus={errors?.kanaName ? 'error' : ''}
                        help={
                            (String(errors?.kanaName?.type) === 'required' &&
                                t('VALIDATION.REQUIRED', { name: t('staff:STAFF_NAME_KANA') })) ||
                            (String(errors?.kanaName?.type) === 'pattern' &&
                                t('VALIDATION.KANA', { name: t('staff:STAFF_NAME_KANA') })) ||
                            (String(errors?.kanaName?.type) === 'maxLength' &&
                                t('VALIDATION.MAXLENGTH', {
                                    name: t('staff:STAFF_NAME_KANA'),
                                    maxLength: 20,
                                }))
                        }
                    >
                        <Controller
                            name="kanaName"
                            control={control}
                            rules={{
                                required: true,
                                pattern: pattern.kana,
                                maxLength: 20,
                            }}
                            render={({ field }) => <InputWrap field={field} />}
                        />
                    </FormItemWrap>
                </Col>
                <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                    <FormItemWrap
                        validateStatus={errors?.stores ? 'error' : ''}
                        help={
                            String(errors?.stores?.type) === 'required' &&
                            t('VALIDATION.REQUIRED_CHOOSE', { name: t('STORE') })
                        }
                        required
                        label={t('STORE')}
                    >
                        <Space size={10}>
                            <Controller
                                name="stores"
                                rules={{
                                    required: true,
                                    maxLength: 14,
                                }}
                                control={control}
                                render={({ field }) => (
                                    <CheckBoxGroupWrap options={plainOptions} field={field} addOnchange={false} />
                                )}
                            />
                        </Space>
                    </FormItemWrap>
                </Col>
                <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                    <FormItemWrap label={t('staff:STORE_MANAGER_AUTHORITY')}>
                        <Space size={10}>
                            <Controller
                                name="storeManagerAuthority"
                                control={control}
                                render={({ field }) => <CheckBoxWrap field={field} />}
                            />
                        </Space>
                    </FormItemWrap>
                </Col>
                <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                    <FormItemWrap
                        label={t('staff:POST_CODE')}
                        validateStatus={errors?.postCode ? 'error' : ''}
                        help={
                            (String(errors?.postCode?.type) === 'maxLength' &&
                                t('VALIDATION.ZIP_CODE', {
                                    name: t('staff:POST_CODE'),
                                    maxLength: 7,
                                })) ||
                            (String(errors?.postCode?.type) === 'pattern' &&
                                t('VALIDATION.ZIP_CODE', { name: t('staff:POST_CODE') }))
                        }
                    >
                        <Controller
                            name="postCode"
                            control={control}
                            rules={{
                                maxLength: 7,
                                pattern: pattern.inputNumber,
                            }}
                            render={({ field }) => <InputOnlyNumber field={field} maxLength={7} />}
                        />
                    </FormItemWrap>
                    <FormItemWrap label={t('staff:PREFECTURES')}>
                        <Controller
                            name="prefectures"
                            control={control}
                            render={({ field }) => (
                                <SelectWrap
                                    field={field}
                                    options={prefectures?.map((prefecture) => ({
                                        id: prefecture,
                                        value: prefecture,
                                    }))}
                                />
                            )}
                        />
                    </FormItemWrap>
                    <FormItemWrap isExplain={false} label={t('staff:ADDRESS')} className="reset__height__address">
                        <Row gutter={32}>
                            <Col span={12}>
                                <FormItemWrap
                                    isExplain
                                    validateStatus={errors?.conscious ? 'error' : ''}
                                    help={
                                        String(errors?.conscious?.type) === 'maxLength' &&
                                        t('VALIDATION.MAXLENGTH', {
                                            name: t('staff:ADDRESS'),
                                            maxLength: 200,
                                        })
                                    }
                                >
                                    <Controller
                                        name="conscious"
                                        control={control}
                                        rules={{ maxLength: 200 }}
                                        render={({ field }) => <InputWrap field={field} />}
                                    />
                                </FormItemWrap>
                            </Col>
                            <Col span={12}>
                                <FormItemWrap
                                    isExplain
                                    validateStatus={errors?.city ? 'error' : ''}
                                    help={
                                        String(errors?.city?.type) === 'maxLength' &&
                                        t('VALIDATION.MAXLENGTH', {
                                            name: t('staff:ADDRESS'),
                                            maxLength: 200,
                                        })
                                    }
                                >
                                    <Controller
                                        name="city"
                                        control={control}
                                        rules={{ maxLength: 200 }}
                                        render={({ field }) => <InputWrap field={field} />}
                                    />
                                </FormItemWrap>
                            </Col>
                        </Row>
                    </FormItemWrap>
                </Col>
                <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                    <FormItemWrap
                        label={t('staff:PHONE_NUMBER')}
                        validateStatus={errors?.phoneNumber ? 'error' : ''}
                        help={
                            String(errors?.phoneNumber?.type) === 'pattern' &&
                            t('VALIDATION.NUMBER_INVALID', { name: t('staff:PHONE_NUMBER') })
                        }
                    >
                        <Controller
                            name="phoneNumber"
                            control={control}
                            rules={{ pattern: pattern.inputNumber }}
                            render={({ field }) => <InputOnlyNumber field={field} maxLength={13} />}
                        />
                    </FormItemWrap>
                    <FormItemWrap
                        label={t('staff:FAX_NUMBER')}
                        validateStatus={errors?.faxNum ? 'error' : ''}
                        help={
                            (String(errors?.faxNum?.type) === 'pattern' &&
                                t('VALIDATION.FAX', { name: t('staff:FAX_NUMBER') })) ||
                            (String(errors?.faxNum?.type) === 'maxLength' &&
                                t('VALIDATION.FAX', { name: t('staff:FAX_NUMBER') }))
                        }
                    >
                        <Controller
                            name="faxNum"
                            control={control}
                            rules={{ maxLength: 13, pattern: pattern.inputNumber }}
                            render={({ field }) => <InputOnlyNumber field={field} maxLength={13} />}
                        />
                    </FormItemWrap>
                </Col>
                <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                    <FormItemWrap
                        label="E-mail"
                        validateStatus={errors?.email ? 'error' : ''}
                        help={
                            (String(errors?.email?.type) === 'pattern' &&
                                t('validateName:ACCOUNT_INVALID', { name: t('staff:email') })) ||
                            (String(errors?.email?.type) === 'maxLength' &&
                                t('VALIDATION.MAXLENGTH', {
                                    name: t('staff:email'),
                                    maxLength: 50,
                                }))
                        }
                    >
                        <Controller
                            name="email"
                            control={control}
                            rules={{
                                pattern: pattern.email,
                                maxLength: 50,
                            }}
                            render={({ field }) => <InputWrap field={field} />}
                        />
                    </FormItemWrap>
                </Col>
                <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                    <FormItemWrap label={t('staff:START_END_JOIN')}>
                        <Controller
                            name="joiningTime"
                            control={control}
                            render={({ field }) => (
                                <DatePicker.RangePicker
                                    {...field}
                                    placeholder={[t('staff:start_time'), t('staff:end_time')]}
                                />
                            )}
                        />
                    </FormItemWrap>
                    <FormItemWrap
                        label={t('staff:SORT_ORDER')}
                        validateStatus={errors?.sortOrder ? 'error' : ''}
                        help={
                            String(errors?.sortOrder?.type) === 'pattern' &&
                            t('VALIDATION.NUMBER_INVALID', { name: t('staff:SORT_ORDER') })
                        }
                    >
                        <Controller
                            name="sortOrder"
                            control={control}
                            rules={{ pattern: pattern.inputNumber }}
                            render={({ field }) => <InputOnlyNumber maxLength={3} field={field} />}
                        />
                    </FormItemWrap>
                </Col>

                <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 6 }}>
                    {!id ? (
                        <Row justify="end">
                            <ButtonWrap
                                isLoading={isLoadingCreate || isLoadingUpdate}
                                htmlType="submit"
                                text={`${t('store-access:REGISTER')}`}
                                type="primary"
                                width={140}
                            />
                        </Row>
                    ) : (
                        <Row justify="end" gutter={16}>
                            <Col>
                                <ButtonWrap
                                    isLoading={isLoadingCreate || isLoadingUpdate}
                                    htmlType="submit"
                                    text={`${t('store-access:UPDATE')}`}
                                    type="primary"
                                    width="140px"
                                />
                            </Col>
                            {/* <Col>
                                <ButtonWrap
                                    isLoading={isLoadingCreate || isLoadingUpdate}
                                    htmlType="submit"
                                    text={`${t('store-access:DELETE')}`}
                                    danger
                                    width="140px"
                                />
                            </Col> */}
                        </Row>
                    )}
                </Col>
            </Form>
            <Col span={24} xl={{ span: 16, offset: 4 }} xxl={{ span: 12, offset: 8 }}>
                <p>
                    {t('staff:REGISTERED_PERSON')} : {storeItem?.data?.createdBy?.name}
                </p>
                <p>
                    {t('staff:REGISTRATION_DATE')} :{' '}
                    {(storeItem?.data?.createdAt && moment(storeItem?.data?.createdAt).format('YYYY-MM-DD HH:mm')) ||
                        ''}
                </p>
                <p>
                    {t('staff:CHANGER')} : {storeItem?.data?.updatedBy?.name}
                </p>
                <p>
                    {t('staff:UPDATE_DATE')} :{' '}
                    {(storeItem?.data?.updatedAt && moment(storeItem?.data?.updatedAt).format('YYYY-MM-DD HH:mm')) ||
                        ''}
                </p>
            </Col>
        </div>
    );
};

export default memo(FormData);
